/* eslint-disable react/jsx-props-no-spreading */
import {
  Button,
  ButtonGroup,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Text,
  VStack,
} from '@chakra-ui/react';
import { FormProvider, useForm } from 'react-hook-form';

import SolidButton from '@/client/components/shared/buttons/SolidButton';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

// gate and prompt must read the same key, and the same one IllustratedConfirmModal uses
const deleteInputSchema = (confirmWord: string) =>
  z.object({
    deleteInputText: z.string().refine((value) => value === confirmWord, { message: 'Invalid input' }),
  });

// TODO: Replace all confirmation modals with this component.

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageSrc: string;
  title: string;
  descriptionOne: string;
  descriptionTwo?: string;
  cancelButtonOptions?: {
    label?: string;
    onClick?: () => void;
  };
  submitButtonOptions: {
    label: string;
    onClick: () => void;
    isLoading?: boolean;
  };
  /** Gate the submit button behind typing DELETE (default). */
  requireTypedConfirmation?: boolean;
}

export default function DeleteConfirmationModal({
  isOpen,
  onClose,
  imageSrc,
  title,
  descriptionOne,
  descriptionTwo,
  cancelButtonOptions = {
    label: 'Cancel',
    onClick: () => onClose(),
  },
  submitButtonOptions,
  requireTypedConfirmation = true,
}: ConfirmationModalProps) {
  const { t } = useTranslation();
  const confirmWord = t('global.confirm.deleteWord');

  const methods = useForm({
    resolver: requireTypedConfirmation ? zodResolver(deleteInputSchema(confirmWord)) : undefined,
    defaultValues: {
      deleteInputText: '',
    },
  });

  const {
    reset,
    register,
    handleSubmit,
    formState: { isValid },
  } = methods;

  const onSubmit = () => {
    reset();
    submitButtonOptions.onClick();
  };

  return (
    <Modal isCentered isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />

      <ModalContent>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <ModalBody paddingTop={12}>
              <VStack textAlign="center">
                <Image src={imageSrc} width={120} height="auto" />

                <Text fontSize="20px" fontWeight={600} color="baseBlack">
                  {title}
                </Text>

                <Text fontSize="14px" fontWeight={500} color="neutral.1000">
                  {descriptionOne}
                </Text>

                {descriptionTwo && (
                  <Text fontSize="14px" fontWeight={500} color="neutral.1000">
                    {descriptionTwo}
                  </Text>
                )}

                {requireTypedConfirmation && (
                  <Input
                    {...register('deleteInputText')}
                    variant="create"
                    placeholder={t('global.confirm.typeToConfirm', { word: confirmWord }) as string}
                  />
                )}
              </VStack>
            </ModalBody>

            <ModalFooter paddingX={10} paddingBottom={8} paddingTop={10}>
              <ButtonGroup width="full" alignItems="center" justifyContent="center">
                <Button variant="createCancel" onClick={cancelButtonOptions.onClick}>
                  {cancelButtonOptions.label}
                </Button>

                <SolidButton
                  type="submit"
                  isLoading={submitButtonOptions.isLoading}
                  isDisabled={requireTypedConfirmation && !isValid}
                >
                  {submitButtonOptions.label}
                </SolidButton>
              </ButtonGroup>
            </ModalFooter>
          </form>
        </FormProvider>
      </ModalContent>
    </Modal>
  );
}
